import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import type { TimesheetEntryRow } from './contractsMirror.js';
import { useAdminEditEntryMutation, useDeleteEntryMutation } from './useApprovalMutation.js';
import { useWeekEntriesQuery } from './useWeekEntriesQuery.js';

/**
 * Correct the shifts behind one employee's week — the supervisor's direct-edit path.
 *
 * The week grid shows per-day TOTALS, which is the right read for approving a week and the wrong
 * one for fixing it: "Tuesday: 8.00" cannot be edited because it may be one shift or three. This
 * panel fetches the shifts themselves and edits them individually.
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): the form sends CLOCK TIMES and never an hours figure. The
 * server derives the hours from the span, applies the lunch rule, and recomputes labor cost, so a
 * supervisor's correction is priced by exactly the same code as a crew punch. It also refuses an
 * approved week and requires the reason, and both are re-checked there regardless of this UI.
 *
 * Every control here is the one this screen already uses (the `CONTROL` field treatment and the
 * kit `Button`), so the edit surface reads as part of Timesheets rather than a second dialect.
 */

/**
 * An ISO instant → the wall-clock string a `datetime-local` input expects. The local getters and
 * `new Date(localValue)` are exact inverses, so a shift round-trips through the form unchanged
 * rather than drifting by the viewer's offset every time it is opened.
 */
function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export interface EditWeekEntriesProps {
  employeeId: number;
  employeeName: string;
  weekStart: string;
  /**
   * Namespaces this panel's field ids. The desktop table and the phone accordion are BOTH in the
   * DOM at all times (one is merely CSS-hidden), so an open row mounts this component twice —
   * without a prefix every input id would exist twice and each `htmlFor` would resolve to the
   * hidden copy as often as the visible one.
   */
  idPrefix: string;
}

export function EditWeekEntries({
  employeeId,
  employeeName,
  weekStart,
  idPrefix,
}: EditWeekEntriesProps) {
  const { t } = useTranslation();
  // Mounted only while the row is open, so the fetch is the mount — a closed row costs nothing
  // and never reaches for a query client it has no reason to need.
  const { data, isLoading, isError } = useWeekEntriesQuery(employeeId, weekStart);

  if (isLoading) {
    return <p className="py-3 text-muted-foreground text-sm">{t('timesheets:edit.loading')}</p>;
  }

  if (isError) {
    return (
      <p role="alert" className="py-3 text-destructive text-sm">
        {t('timesheets:edit.loadFailed')}
      </p>
    );
  }

  const entries = data?.timesheets ?? [];

  // The server refuses to edit inside an approved week, so say why up front instead of letting
  // somebody retype a shift and meet a rejection at the end of it.
  if (data?.summary?.weekApproved) {
    return <p className="py-3 text-muted-foreground text-sm">{t('timesheets:edit.weekLocked')}</p>;
  }

  if (entries.length === 0) {
    return <p className="py-3 text-muted-foreground text-sm">{t('timesheets:edit.noEntries')}</p>;
  }

  return (
    <div className="grid gap-3 py-3">
      <p className="text-muted-foreground text-sm">
        {t('timesheets:edit.intro', { name: employeeName })}
      </p>
      {entries.map((entry) => (
        <EntryRow key={entry.id} entry={entry} idPrefix={idPrefix} />
      ))}
    </div>
  );
}

/**
 * One shift. Open shifts (still clocked in) are not editable here — those are closed from the
 * active-clock list, which is the path that knows how to book a missing clock-out.
 */
function EntryRow({ entry, idPrefix }: { entry: TimesheetEntryRow; idPrefix: string }) {
  const { t } = useTranslation();
  const fieldId = (name: string) => `${idPrefix}-${name}-${entry.id}`;
  const editEntry = useAdminEditEntryMutation();
  const deleteEntry = useDeleteEntryMutation();

  const [clockInLocal, setClockInLocal] = useState(() => toLocalInputValue(entry.clockInAt));
  const [clockOutLocal, setClockOutLocal] = useState(() => toLocalInputValue(entry.clockOutAt));
  const [note, setNote] = useState(entry.note ?? '');
  const [reason, setReason] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const mutationError = editEntry.error ?? deleteEntry.error;
  const errorMessage =
    mutationError instanceof ApiError
      ? t(`timesheets:edit.errors.${mutationError.message}`, {
          defaultValue: t('timesheets:edit.errors.generic'),
        })
      : mutationError
        ? t('timesheets:edit.errors.generic')
        : null;

  const isOpenShift = !entry.clockOutAt;
  const complete = Boolean(clockInLocal && clockOutLocal && reason.trim());
  const busy = editEntry.isPending || deleteEntry.isPending;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!complete) return;
    editEntry.mutate(
      {
        entryId: entry.id,
        // datetime-local is wall-clock; the server stores and compares instants.
        clockInAt: new Date(clockInLocal).toISOString(),
        clockOutAt: new Date(clockOutLocal).toISOString(),
        editReason: reason.trim(),
        note: note.trim() || null,
      },
      { onSuccess: () => setReason('') },
    );
  }

  return (
    <form
      className="rounded-md border border-border bg-background p-3"
      onSubmit={submit}
      aria-label={t('timesheets:edit.shiftLabel', { date: entry.date })}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-foreground text-sm">{entry.date}</span>
        <span className="font-mono text-foreground text-sm">
          {Number(entry.hours || 0).toFixed(2)}
        </span>
        <span className="min-w-0 truncate text-muted-foreground text-sm">
          {entry.jobName ?? t('timesheets:edit.generalTime')}
        </span>
      </div>

      {isOpenShift ? (
        <p className="mt-2 text-muted-foreground text-sm">{t('timesheets:edit.stillClockedIn')}</p>
      ) : (
        <>
          {/* One column per field on a phone, four across from `sm` — every shift's fields land on
              the same lines as every other shift's, so the list reads as a table, not a ragged pile. */}
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field id={fieldId('in')} label={t('timesheets:edit.clockInLabel')}>
              <input
                id={fieldId('in')}
                type="datetime-local"
                className={CONTROL}
                value={clockInLocal}
                onChange={(event) => setClockInLocal(event.target.value)}
                required
              />
            </Field>
            <Field id={fieldId('out')} label={t('timesheets:edit.clockOutLabel')}>
              <input
                id={fieldId('out')}
                type="datetime-local"
                className={CONTROL}
                value={clockOutLocal}
                onChange={(event) => setClockOutLocal(event.target.value)}
                required
              />
            </Field>
            <Field id={fieldId('note')} label={t('timesheets:edit.noteLabel')}>
              <input
                id={fieldId('note')}
                type="text"
                className={CONTROL}
                value={note}
                maxLength={500}
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
            <Field id={fieldId('reason')} label={t('timesheets:edit.reasonLabel')}>
              <input
                id={fieldId('reason')}
                type="text"
                className={CONTROL}
                value={reason}
                maxLength={500}
                placeholder={t('timesheets:edit.reasonPlaceholder')}
                onChange={(event) => setReason(event.target.value)}
                required
              />
            </Field>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" disabled={busy || !complete}>
              {t('timesheets:edit.save')}
            </Button>
            {confirmingRemove ? (
              <>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={busy}
                  onClick={() => deleteEntry.mutate({ entryId: entry.id })}
                >
                  {t('timesheets:edit.confirmRemove')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingRemove(false)}
                >
                  {t('timesheets:edit.cancelRemove')}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  deleteEntry.reset();
                  setConfirmingRemove(true);
                }}
              >
                {t('timesheets:edit.remove')}
              </Button>
            )}
            {editEntry.isSuccess && !editEntry.isPending ? (
              <span role="status" className="text-muted-foreground text-sm">
                {t('timesheets:edit.saved')}
              </span>
            ) : null}
          </div>
        </>
      )}

      {errorMessage ? (
        <p role="alert" aria-live="assertive" className="mt-2 text-destructive text-sm">
          {errorMessage}
        </p>
      ) : null}
    </form>
  );
}

/** The one control treatment on this screen, matching AddEntry and the resolve-open form. */
const CONTROL =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  // `min-w-0` for the same reason AddEntry carries it: a flex/grid child defaults to its own
  // min-content width, and a datetime input's intrinsic width is wider than a phone.
  return (
    <div className="w-full min-w-0">
      <label htmlFor={id} className="font-medium text-foreground text-xs">
        {label}
      </label>
      {children}
    </div>
  );
}
